import { useReactiveVar } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { Editor } from "@tiptap/core";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  MeetingParticipantNode,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  TopicNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import cache, { currentTiptapJWTVar, currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Error from "@components/error/error";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { cursorColors, isEmptyValue } from "@components/wysiwyg/helpers";
import WYSIWYG from "@components/wysiwyg/wysiwyg";
import { tempCacheIdPrefix } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, parseStringToJSON } from "@helpers/helpers";

import { MeetingWebsocketProviderContext } from "../context";

const IndividualNotes = ({
  topic,
  meeting,
  participant,
  onModEnter,
  isTopicInteracting,
  onWebsocketMessageChange,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  participant: Pick<MeetingParticipantNode, "participantEmail" | "id"> & {
    user?: { id: number; firstName: string; name: string } | null;
  };
  onModEnter?: () => void;
  isTopicInteracting?: boolean;
  onWebsocketMessageChange?: (key: string, message: string | null) => void;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const currentUser = currentUserVar();
  const isTemporary = String(topic.id).includes(tempCacheIdPrefix);
  const isFormalOneonone = !!meeting.meetingGroup?.isFormalOneonone;
  const individualNotes = assertEdgesNonNull(topic.individualNotes) || [];

  const participantIsCurrentUser = participant.user?.id === currentUser.id;
  const participantIndividualNote = individualNotes.find(
    ({ creator }) => creator.id === participant?.user?.id,
  );
  const isNoteHidden = participantIndividualNote
    ? participantIndividualNote.hidden
    : true; // if the current note is not visible, then it means that it is private therefore hidden.

  const noteMarkedAsPrivate = participantIsCurrentUser ? isNoteHidden : false;

  const note = participantIndividualNote?.notes || "";
  const canEdit = participantIsCurrentUser && topic.canUpdate.permission;
  // Treat the note as "empty" only when both the prop (backend snapshot)
  // and the local editor agree, since `cache.modify` uses broadcast:
  // false and the prop can lag behind keystrokes.
  const propNoteIsEmpty = isEmptyValue(parseStringToJSON(note));
  const [localNoteIsEmpty, setLocalNoteIsEmpty] = useState(propNoteIsEmpty);
  const noteIsTrulyEmpty = propNoteIsEmpty && localNoteIsEmpty;
  // Hide other participants' empty notes always; hide the current user's
  // empty notes only when the user isn't focused/interacting with the
  // topic (so they can see the placeholder when they click in).
  const isHiddenWhenEmpty = participantIsCurrentUser
    ? !isTopicInteracting
    : true;
  const shouldHideForEmpty = noteIsTrulyEmpty && isHiddenWhenEmpty;

  let overlayValue = null;
  if (isFormalOneonone && isEmptyValue(note) && topic.linkedTemplateTopic?.id) {
    const defaultNotes =
      meeting.meetingGroup?.facilitator?.id === participant.user?.id
        ? topic.linkedTemplateTopic.defaultFacilitatorNotes
        : topic.linkedTemplateTopic.defaultSubjectNotes;
    if (defaultNotes) overlayValue = parseStringToJSON(defaultNotes);
  }

  const handleUpdateContent = ({ editor }: { editor: Editor }) => {
    setLocalNoteIsEmpty(isEmptyValue(editor.getJSON()));
    if (canEdit && participantIndividualNote && providerWebsocket?.isSynced) {
      cache.modify({
        id: cache.identify(participantIndividualNote),
        fields: {
          notes() {
            return editor.getJSON();
          },
        },
        // without broadcast false, it would update the active queries right away and
        // re-render everytime the user makes a change.
        broadcast: false,
      });
    }
  };

  const suffix = noteMarkedAsPrivate ? "-private" : "";
  const userId = participant.user?.id;
  const websocketId = `meeting-${meeting?.id}-participant-${userId}${suffix}`;
  const websocketDocumentId = `topic-${topic.id}-participant-${userId}${suffix}`;
  const context = useContext(MeetingWebsocketProviderContext);
  const { ydoc, providerWebsocket } = context[`${websocketId}`] || {
    ydoc: undefined,
    providerWebsocket: null,
  };

  const handleWebsocketMessageChange = useCallback(
    (message: string | null) => {
      onWebsocketMessageChange?.(websocketDocumentId, message);
    },
    [onWebsocketMessageChange, websocketDocumentId],
  );

  const tiptapAiJwt = useReactiveVar(currentTiptapJWTVar);

  const uploadVariable = useMemo(() => ({ topicId: topic.id }), [topic.id]);
  const mentionsConfig = useMemo(
    () => ({
      meetingGroupId: meeting?.meetingGroup?.id,
      meetingId: meeting?.id,
    }),
    [meeting?.id, meeting?.meetingGroup?.id],
  );
  const extraContext = useMemo(
    () => ({
      topicId: topic.id,
      relatedTopicId: !noteMarkedAsPrivate ? topic.id : undefined,
      meetingId: meeting.id,
      meetingGroupId: meeting.meetingGroup?.id,
      organizationId: meeting.organization?.id,
      meetingDate: meeting.startDatetime ? meeting.startDatetime : undefined,
    }),
    [
      noteMarkedAsPrivate,
      meeting.id,
      meeting.meetingGroup?.id,
      meeting.organization?.id,
      meeting.startDatetime,
      topic.id,
    ],
  );

  // Keep the editor mounted (rather than `return null`) so the Yjs doc can
  // sync and report content via `onEmptyChange` even before the GraphQL
  // snapshot reflects it. When the slot should be collapsed, hide it
  // visually with `hidden` instead of unmounting.
  const participantCursorColor = participant.user?.id
    ? cursorColors[participant.user.id % cursorColors.length]
    : cursorColors[0];

  return (
    <div
      key={participant.id}
      className={classNames("flex mt-4", shouldHideForEmpty && "hidden")}
      aria-label="Meeting topic individual notes"
      ref={ref}
    >
      <div className="pr-3 flex-shrink-0">
        <div
          className="w-0.5 h-full opacity-50"
          style={{ backgroundColor: participantCursorColor }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center pb-1">
          <Avatar user={participant.user} size={4} />
          {participant.user?.firstName && (
            <span className="ml-2 tf-header-sm">
              {participant.user.firstName}
            </span>
          )}
        </div>
        <div className="min-w-0">
          {isTemporary || ydoc === undefined || providerWebsocket === null ? (
            <div className="min-h-[28px] flex items-center">
              <Loading size="5" />
            </div>
          ) : isNoteHidden && !participantIsCurrentUser ? (
            <div className="italic text-xs text-gray-500 flex items-center gap-1 py-2">
              <TopicflowIcon type="eyeOff" className="h-4 w-4 text-gray-400" />
              Notes are private.
            </div>
          ) : (
            <ErrorBoundary
              fallback={
                <Error
                  title="An unexpected error occurred."
                  description={
                    "The editor could not be rendered. We have been notified. Please refresh the page."
                  }
                />
              }
            >
              <div className="relative">
                <div
                  id={websocketDocumentId}
                  // Do not remove the key with tiptapAiJwt, editor needs to be recreated when the tiptapAiJwt changes
                  key={`${websocketDocumentId}-${tiptapAiJwt}`}
                >
                  <WYSIWYG
                    key={websocketDocumentId}
                    enableComment={false}
                    value={parseStringToJSON(note)}
                    className={classNames(
                      "js-individual-notes js-meeting-topic-input",
                      "text-sm",
                      // See the matching comment in discussion-notes.tsx —
                      // keep the WYSIWYG's default `pl-7` so the drag-handle
                      // gutter is inside the editor's mouseleave region,
                      // shift the element back with `-ml-7` so the text
                      // stays visually flush, and `max-w-none` widens the
                      // box to recover the right edge that the negative
                      // margin would otherwise leave 28px short.
                      "-ml-7 max-w-none",
                      // Block spacing inside topic notes editors is handled
                      // globally in index.css under
                      // `.js-meeting-topic .ProseMirror`.
                    )}
                    editable={canEdit}
                    placeholder={
                      canEdit ? "Add your own notes, '/' for commands" : ""
                    }
                    overlayValue={overlayValue}
                    showPlaceholderOnlyWhenEditable={false}
                    showPlusButton={canEdit}
                    uploadVariable={uploadVariable}
                    ydoc={ydoc}
                    providerWebsocket={providerWebsocket}
                    webSocketDocumentId={websocketDocumentId}
                    mentionsConfig={mentionsConfig}
                    extraContext={extraContext}
                    onUpdateContent={handleUpdateContent}
                    tiptapAiJwt={tiptapAiJwt}
                    onModEnter={onModEnter}
                    onEnter={onModEnter}
                    hideStatusBanner
                    onWebsocketMessageChange={handleWebsocketMessageChange}
                  />
                </div>
              </div>
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndividualNotes;
